<template>
  <div v-if="currentProject">
    <h2 class="text-base font-medium text-gray-600 mb-2">Project</h2>
    <ProjectManagerCurrentProject :project="currentProject" @remove="removeCurrentProject" />
  </div>
  <div v-else>
    <div class="relative text-gray-400 focus-within:text-gray-500">
      <BaseFormInput
        type="search"
        v-model="search"
        placeholder="search"
        class="pl-10 bg-white text-gray-900"
        @input="searchProjects"
      />
      <div class="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center">
        <svg
          class="h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fill-rule="evenodd"
            d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817
            4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
            clip-rule="evenodd"
          />
        </svg>
      </div>
    </div>
    <div v-if="tags.length > 0" class="mt-4">
      <BaseFormGroup>
        <BaseFormLabel> Industry </BaseFormLabel>
        <VMultiselect
          v-model="selectedTags"
          :options="tags"
          track-by="id"
          :close-on-select="false"
          :multiple="true"
          placeholder="Filter by industry"
          label="name"
          @input="searchProjects"
        >
          <template #option="{ option }">
            {{ option.name }}
          </template>
        </VMultiselect>
      </BaseFormGroup>
    </div>
    <ul
      role="list"
      class="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 xl:gap-x-8 mt-12"
    >
      <li v-for="project in projects" :key="project.uuid" class="relative">
        <ProjectManagerTile :project="project" @view="viewProject" />
      </li>
    </ul>
    <div v-if="hasMore" class="text-center mt-8">
      <BaseButton primary @click="loadMore">
        <Loader v-if="isStatusPending" small inverse />
        <span v-else>Load more</span>
      </BaseButton>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash-es';
import ProjectManagerTile from './ProjectManagerTile.vue';
import ProjectManagerCurrentProject from './ProjectManagerCurrentProject.vue';

const STATUS_IDLE = 'STATUS_IDLE';
const STATUS_INITIALIZING = 'STATUS_INITIALIZING';
const STATUS_PENDING = 'STATUS_PENDING';
const STATUS_RESOLVED = 'STATUS_RESOLVED';
const STATUS_REJECTED = 'STATUS_REJECTED';

export default {
  components: { ProjectManagerTile, ProjectManagerCurrentProject },
  data() {
    return {
      status: STATUS_IDLE,
      search: '',
      selectedTagIds: [],
      tagIds: [],
      ids: [],
      meta: null,
      current: null,
    };
  },
  computed: {
    isStatusPending() {
      return this.status === STATUS_PENDING;
    },
    currentProject() {
      if (!this.current) {
        return null;
      }

      return this.$store.state.entities.projects.byId[this.current] || null;
    },
    tags() {
      return this.tagIds.map((id) => this.$store.state.entities.tags.byId[id]).filter((t) => t);
    },
    selectedTags: {
      get() {
        return this.selectedTagIds
          .map((id) => this.$store.state.entities.tags.byId[id])
          .filter((t) => t);
      },
      set(tags) {
        this.selectedTagIds = tags.map((tag) => tag.id);
      },
    },
    projects() {
      return this.ids.map((id) => this.$store.state.entities.projects.byId[id]).filter((p) => p);
    },
    hasMore() {
      if (!this.meta) {
        return false;
      }

      return this.meta?.current_page < this.meta?.last_page;
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.status = STATUS_INITIALIZING;
      this.fetchIndustryTags();
      this.fetchProjects();
    },
    loadMore() {
      if (this.isStatusPending) {
        return;
      }

      this.status = STATUS_PENDING;
      this.fetchProjects(
        {
          filter: {
            search: this.search,
            client_tag_in: this.selectedTagIds.join(','),
          },
          page: (this.meta?.current_page || 0) + 1,
        },
        true
      );
    },
    // eslint-disable-next-line func-names
    searchProjects: debounce(
      function () {
        if (this.isStatusPending) {
          return;
        }

        this.status = STATUS_PENDING;
        this.fetchProjects({
          filter: {
            search: this.search,
            client_tag_in: this.selectedTagIds.join(','),
          },
        });
      },
      400,
      { trailing: true }
    ),
    async fetchIndustryTags() {
      try {
        const { tagIds } = await this.$store.dispatch('ui/tags/load', {
          params: {
            filter: {
              has_accounts: true,
              category: 'Industry',
            },
          },
        });

        this.tagIds = tagIds;
      } catch (error) {
        //
      }
    },
    async fetchProjects({ filter = {}, page = 1 } = {}, append = false) {
      try {
        const { ids, meta } = await this.$store.dispatch('ui/projects/load', {
          params: {
            filter: {
              ...filter,
              has_assets: true,
            },
            page,
            include: 'client',
          },
        });

        this.ids = append ? [...this.ids, ...ids] : ids;
        this.meta = meta;

        this.status = STATUS_RESOLVED;
      } catch (error) {
        this.status = STATUS_REJECTED;
      }
    },
    viewProject(project) {
      this.current = project.uuid;
      this.$emit('view-project', project);
    },
    removeCurrentProject() {
      this.current = null;
      this.$emit('clear-project');
    },
  },
};
</script>
